/**
 * Ported VERBATIM from `apr_template/src/pages/ai-analysis/AnalysisResults.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../compat/PageHeader';
import PanelCloseButton from '../components/PanelCloseButton';
import InfoTip from '../compat/InfoTip';
import HelpPanel from '../compat/HelpPanel';
import { type Help } from '../compat/helpTypes';
import {
  type AgentRunDetail,
  type AgentRunApp,
  type AgentRunTheme,
  type ImportDiff,
  type ImportPreview,
  cancelRun,
  deleteRun,
  getRun,
  getProgress,
  listRuns,
  overrideApp,
  resumeClientAnalysis,
  downloadXlsx,
  previewImport,
  applyImport,
} from '../compat/aiRunsApi';
import { getRunCurrent, downloadReport, type CurrentReport } from '../compat/reportsApi';
import { getAppAnalyses, pinAnalysis, unpinAnalysis } from '../compat/aiRepositoryApi';
import { useAuth } from '../compat/AuthContext';
import ClientAnalysisImportModal from './ClientAnalysisImportModal';

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function fmt$(v?: number | null): string {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
}

// A one-time cost shown as a low–high range (with the mid) when a range exists,
// else the single mid value.
function fmtCostRange(mid?: number | null, low?: number | null, high?: number | null): string {
  const hasRange = low != null || high != null;
  if (!hasRange) return fmt$(mid);
  const base = `${fmt$(low)} – ${fmt$(high)}`;
  return mid != null ? `${base}  ·  mid ${fmt$(mid)}` : base;
}

function fmtPct(v?: number | null): string {
  if (v == null) return '—';
  return `${v.toFixed(1)}%`;
}

function fmtNum(v?: number | null): string {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US').format(v);
}

/** Minimal markdown renderer: supports # headings, **bold**, bullet lists, numbered lists, paragraphs. */
function renderMd(md?: string | null): React.ReactNode {
  if (!md) return null;
  const lines = md.split('\n');
  const nodes: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const flushList = () => {
    if (!listItems.length) return;
    const items = listItems.map((li, i) => (
      <li key={i} dangerouslySetInnerHTML={{ __html: applyInline(li) }} />
    ));
    nodes.push(listType === 'ol'
      ? <ol key={nodes.length} style={{ paddingLeft: 20, margin: '6px 0' }}>{items}</ol>
      : <ul key={nodes.length} style={{ paddingLeft: 20, margin: '6px 0' }}>{items}</ul>
    );
    listItems = [];
    listType = null;
  };

  lines.forEach((line, idx) => {
    const h3 = line.match(/^### (.+)/);
    const h2 = line.match(/^## (.+)/);
    const h1 = line.match(/^# (.+)/);
    const ul = line.match(/^[-*] (.+)/);
    const ol = line.match(/^\d+\. (.+)/);

    if (h1 || h2 || h3) {
      flushList();
      const text = (h1 || h2 || h3)![1] ?? '';
      const tag = h1 ? 'h3' : h2 ? 'h4' : 'h5';
      nodes.push(React.createElement(tag, {
        key: idx,
        style: { margin: '12px 0 4px', fontWeight: 600 },
        dangerouslySetInnerHTML: { __html: applyInline(text) },
      }));
    } else if (ul) {
      if (listType === 'ol') flushList();
      listType = 'ul';
      listItems.push(ul[1] ?? '');
    } else if (ol) {
      if (listType === 'ul') flushList();
      listType = 'ol';
      listItems.push(ol[1] ?? '');
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      nodes.push(
        <p key={idx} style={{ margin: '6px 0', lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: applyInline(line) }} />
      );
    }
  });
  flushList();
  return <>{nodes}</>;
}

function applyInline(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:#f5f5f5;padding:1px 4px;border-radius:3px;font-size:0.9em">$1</code>');
}

type Disposition = 'RETAIN' | 'RENEGOTIATE' | 'REPLACE' | 'CONSOLIDATE' | 'NEEDS_REVIEW' | string;

// How an analysis was produced — small provenance badge.
const ORIGIN_META: Record<string, { label: string; bg: string; fg: string; bd: string; title: string }> = {
  ai_generated:    { label: 'Baseline Assessment', bg: '#e8eefc', fg: '#3538cd', bd: '#c7d2fe', title: 'Baseline assessment generated by the Claude API' },
  client_document: { label: 'Client', bg: '#e4f4ea', fg: '#17914f', bd: '#bfe6cd', title: 'Interpreted from a client-provided document' },
  consolidated:    { label: 'Merged', bg: '#f3e8fd', fg: '#7b1fa2', bd: '#e4c9f5', title: 'Consolidated: client analysis merged with the existing AI analysis' },
  manual:          { label: 'Manual', bg: '#eef1f5', fg: '#55627a', bd: '#dbe1ea', title: 'Manually entered analysis' },
};
function OriginBadge({ origin }: { origin?: string }) {
  const s = ORIGIN_META[origin || 'ai_generated'] || ORIGIN_META.ai_generated!;
  return (
    <span title={s.title} style={{ fontSize: 11, fontWeight: 700, color: s.fg, background: s.bg, border: `1px solid ${s.bd}`, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function dispositionPillClass(d?: Disposition): string {
  if (!d) return 'rd-pill rd-pill-neutral';
  switch (d.toUpperCase()) {
    case 'RETAIN':      return 'rd-pill rd-pill-info';
    case 'RETAIN+RENEGOTIATE': return 'rd-pill rd-pill-warning';
    case 'RENEGOTIATE': return 'rd-pill rd-pill-warning';  // legacy rows only
    case 'REPLACE':     return 'rd-pill rd-pill-danger';
    case 'CONSOLIDATE': return 'rd-pill rd-pill-purple';
    case 'CLOUD MIGRATION': return 'rd-pill rd-pill-info';
    case 'RE-DESIGN+MODERNIZE': return 'rd-pill rd-pill-purple';
    case 'NEEDS_REVIEW':return 'rd-pill rd-pill-neutral';
    case 'INSUFFICIENT_DATA': return 'rd-pill rd-pill-warning';
    default:            return 'rd-pill rd-pill-neutral';
  }
}

// Human-friendly disposition label (e.g. INSUFFICIENT_DATA → "Insufficient Data").
function dispLabel(d?: string | null): string {
  if (!d) return '—';
  if (d.toUpperCase() === 'INSUFFICIENT_DATA') return 'Insufficient Data';
  if (d.toUpperCase() === 'NEEDS_REVIEW') return 'Needs Review';
  return d;
}

function confidencePillClass(c?: string): string {
  switch (c) {
    case 'high':         return 'rd-pill rd-pill-success';
    case 'medium':       return 'rd-pill rd-pill-warning';
    case 'low':          return 'rd-pill rd-pill-danger';
    case 'needs_review': return 'rd-pill rd-pill-neutral';
    default:             return 'rd-pill rd-pill-neutral';
  }
}

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

/* ------------------------------------------------------------------ */
/* Sub-components                                                       */
/* ------------------------------------------------------------------ */

/* ---- Status banner ---- */
function StatusBanner({
  run,
  onCancel,
}: {
  run: AgentRunDetail;
  onCancel: () => void;
}) {
  if (run.status === 'completed') return null;

  if (run.status === 'failed') {
    return (
      <div style={{
        margin: '12px 20px',
        padding: '14px 18px',
        background: '#ffebee',
        border: '1px solid #ffcdd2',
        borderRadius: 8,
        color: '#c62828',
        fontSize: 14,
      }}>
        <strong>Run failed:</strong> {run.error_message || 'An unexpected error occurred.'}
      </div>
    );
  }

  if (run.status === 'cancelled') {
    return (
      <div style={{
        margin: '12px 20px',
        padding: '14px 18px',
        background: '#f5f5f5',
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        color: '#616161',
        fontSize: 14,
      }}>
        This run was cancelled.
      </div>
    );
  }

  return (
    <div style={{
      margin: '12px 20px',
      padding: '14px 18px',
      background: '#e3f2fd',
      border: '1px solid #bbdefb',
      borderRadius: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: '#1565c0' }}>
          {run.status.charAt(0).toUpperCase() + run.status.slice(1)}&hellip;
        </span>
        <span style={{ fontSize: 13, color: '#1565c0' }}>{run.progress_pct}%</span>
      </div>
      <div style={{ height: 8, background: '#bbdefb', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{
          height: '100%',
          width: `${run.progress_pct}%`,
          background: '#1565c0',
          borderRadius: 4,
          transition: 'width 0.4s ease',
        }} />
      </div>
      {run.status_message && (
        <p style={{ fontSize: 13, color: '#1565c0', margin: 0 }}>{run.status_message}</p>
      )}
      <button
        className="rd-btn rd-btn-ghost"
        style={{ marginTop: 10, fontSize: 12 }}
        onClick={onCancel}
      >
        Cancel run
      </button>
    </div>
  );
}

/* ---- AI Suggestions callout ---- */
function AISuggestionsPanel({ apps }: { apps: AgentRunApp[] }) {
  const top3 = [...apps]
    .filter(a => a.savings_mid != null)
    .sort((a, b) => (b.savings_mid ?? 0) - (a.savings_mid ?? 0))
    .slice(0, 3);

  if (!top3.length) return null;

  return (
    <div style={{
      margin: '12px 20px 0',
      padding: '16px 20px',
      background: 'linear-gradient(135deg, #f3e5f5 0%, #e3f2fd 100%)',
      border: '1px solid #ce93d8',
      borderRadius: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <i className="material-icons" style={{ fontSize: 20, color: '#7b1fa2' }}>auto_awesome</i>
        <span style={{ fontWeight: 600, fontSize: 14, color: '#4a148c' }}>Top savings opportunities</span>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {top3.map((app) => (
          <div key={app.id} style={{
            flex: '1 1 200px',
            background: 'rgba(255,255,255,0.75)',
            border: '1px solid #e1bee7',
            borderRadius: 6,
            padding: '10px 14px',
          }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#212121', marginBottom: 4 }}>
              {app.application_name}
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
              {app.vendor || 'Unknown vendor'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className={dispositionPillClass(app.disposition)}>{dispLabel(app.disposition)}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#2e7d32' }}>
                {fmt$(app.savings_mid)} mid savings
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Override form ---- */
interface OverrideFormProps {
  app: AgentRunApp;
  runId: string;
  onSaved: (updated: Partial<AgentRunApp>) => void;
  onCancel: () => void;
}

const DISPOSITIONS = ['RETAIN', 'RENEGOTIATE', 'REPLACE', 'CONSOLIDATE', 'NEEDS_REVIEW'];

function OverrideForm({ app, runId, onSaved, onCancel }: OverrideFormProps) {
  const [disposition, setDisposition] = useState(app.disposition || '');
  const [justification, setJustification] = useState(app.disposition_justification || '');
  const [savingsLow, setSavingsLow] = useState(app.savings_low != null ? String(app.savings_low) : '');
  const [savingsMid, setSavingsMid] = useState(app.savings_mid != null ? String(app.savings_mid) : '');
  const [savingsHigh, setSavingsHigh] = useState(app.savings_high != null ? String(app.savings_high) : '');
  const [overrideNote, setOverrideNote] = useState(app.override_note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await overrideApp(runId, app.id, {
        disposition,
        disposition_justification: justification,
        savings_low: savingsLow !== '' ? Number(savingsLow) : undefined,
        savings_mid: savingsMid !== '' ? Number(savingsMid) : undefined,
        savings_high: savingsHigh !== '' ? Number(savingsHigh) : undefined,
        override_note: overrideNote,
      });
      onSaved({
        disposition,
        disposition_justification: justification,
        savings_low: savingsLow !== '' ? Number(savingsLow) : undefined,
        savings_mid: savingsMid !== '' ? Number(savingsMid) : undefined,
        savings_high: savingsHigh !== '' ? Number(savingsHigh) : undefined,
        override_note: overrideNote,
        overridden: true,
        overridden_at: new Date().toISOString(),
      });
    } catch {
      setError('Failed to save override. Please try again.');
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'inherit',
    background: '#fff',
    marginTop: 4,
    boxSizing: 'border-box',
  };

  return (
    <div style={{ marginTop: 16, padding: 16, background: '#fffde7', border: '1px solid #fff176', borderRadius: 8 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: '#f57f17' }}>
        Override recommendation
      </div>

      <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 10 }}>
        Disposition
        <select
          value={disposition}
          onChange={e => setDisposition(e.target.value)}
          style={{ ...inputStyle, height: 34 }}
        >
          <option value="">— select —</option>
          {DISPOSITIONS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </label>

      <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 10 }}>
        Justification
        <textarea
          rows={3}
          value={justification}
          onChange={e => setJustification(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
        {([['Low savings ($)', savingsLow, setSavingsLow], ['Mid savings ($)', savingsMid, setSavingsMid], ['High savings ($)', savingsHigh, setSavingsHigh]] as [string, string, (v: string) => void][]).map(([label, val, setter]) => (
          <label key={label} style={{ display: 'block', fontSize: 12, color: '#666' }}>
            {label}
            <input
              type="number"
              value={val}
              onChange={e => setter(e.target.value)}
              style={inputStyle}
            />
          </label>
        ))}
      </div>

      <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 12 }}>
        Override note (internal)
        <textarea
          rows={2}
          value={overrideNote}
          onChange={e => setOverrideNote(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </label>

      {error && <div style={{ fontSize: 12, color: '#c62828', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="rd-btn rd-btn-warning" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save override'}
        </button>
        <button className="rd-btn rd-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ---- App detail drawer ---- */
interface DrawerProps {
  app: AgentRunApp;
  runId: string;
  onClose: () => void;
  onUpdated: (id: string, patch: Partial<AgentRunApp>) => void;
}

function AppDrawer({ app: initialApp, runId, onClose, onUpdated }: DrawerProps) {
  const [app, setApp] = useState(initialApp);
  const [showOverride, setShowOverride] = useState(false);
  // Same two-step panel as the Analysis Repository: a summary you can pin from,
  // then the full analysis. A row click lands on 'summary', never straight in the detail.
  const [view, setView] = useState<'summary' | 'full'>('summary');
  const { currentUser } = useAuth();
  const canPin = currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst';
  const [pinnedRunAppId, setPinnedRunAppId] = useState<string | null>(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinErr, setPinErr] = useState('');
  const isPinned = pinnedRunAppId != null && pinnedRunAppId === app.id;

  useEffect(() => { setApp(initialApp); setView('summary'); }, [initialApp]);

  // Which analysis is pinned as this application's current one.
  const loadPin = useCallback(async () => {
    try {
      const d = await getAppAnalyses(app.application_id);
      setPinnedRunAppId(d.pinnedRunAppId ?? null);
    } catch { /* pinning just stays unavailable */ }
  }, [app.application_id]);
  useEffect(() => { loadPin(); }, [loadPin]);

  const doPin = async () => {
    setPinBusy(true); setPinErr('');
    try { await pinAnalysis(app.application_id, app.id); await loadPin(); }
    catch (e: any) { setPinErr(e?.response?.data?.error || e?.message || 'Could not pin.'); }
    finally { setPinBusy(false); }
  };
  const doUnpin = async () => {
    setPinBusy(true); setPinErr('');
    try { await unpinAnalysis(app.application_id); await loadPin(); }
    catch (e: any) { setPinErr(e?.response?.data?.error || e?.message || 'Could not unpin.'); }
    finally { setPinBusy(false); }
  };

  const handleSaved = (patch: Partial<AgentRunApp>) => {
    const updated = { ...app, ...patch };
    setApp(updated);
    onUpdated(app.id, patch);
    setShowOverride(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 1200,
        }}
      />
      <PanelCloseButton onClose={onClose} panelWidth="min(520px, 92vw)" />
      {/* Drawer panel */}
      <div style={{
        position: 'fixed',
        top: 0, right: 0, bottom: 0,
        width: 'min(520px, 92vw)',
        background: '#fff',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
        zIndex: 1201,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Drawer header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          position: 'sticky', top: 0,
          background: '#fff',
          zIndex: 1,
        }}>
          <div>
            {view === 'full' && (
              <button
                onClick={() => setView('summary')}
                title="Back to the analysis summary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, marginBottom: 6, color: '#1565c0', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
              >
                <i className="material-icons" style={{ fontSize: 16 }}>arrow_back</i>
                Back
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              {app.application_id_code && (
                <span style={{ fontWeight: 700, fontSize: 13, color: '#1565c0' }}>{app.application_id_code}</span>
              )}
              <div style={{ fontWeight: 700, fontSize: 16, color: '#212121' }}>{app.application_name}</div>
            </div>
            {app.vendor && <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{app.vendor}</div>}
            <a
              href={`/customers/applications?appId=${app.application_id}`}
              style={{ fontSize: 12, color: '#1565c0', textDecoration: 'none', fontWeight: 600, marginTop: 4, display: 'inline-block' }}
            >
              Open in Application Master Data →
            </a>
          </div>
        </div>

        {/* Summary view — what a row click opens. Pin from here, or open the full analysis. */}
        {view === 'summary' && (
          <div style={{ padding: '16px 20px', flex: 1 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
              {app.excluded_from_apr ? (
                <span className="rd-pill" style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }}>
                  Not relevant for disposition
                </span>
              ) : (
                <span className={dispositionPillClass(app.disposition)}>{app.disposition ? dispLabel(app.disposition) : 'No disposition'}</span>
              )}
              {!app.excluded_from_apr && app.confidence && <span className={confidencePillClass(app.confidence)}>{app.confidence}</span>}
              {app.overridden && (
                <span className="rd-pill" style={{ background: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082' }}>Overridden</span>
              )}
              {isPinned && <span title="Pinned as this application's current analysis" style={{ fontSize: 12 }}>📌 pinned</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 18 }}>
              {([
                ['Current $/yr', fmt$(app.current_annual_cost)],
                ['Savings', fmtCostRange(app.savings_mid, app.savings_low, app.savings_high)],
                ['One-time cost', fmtCostRange(app.total_one_time_cost, app.total_one_time_cost_low, app.total_one_time_cost_high)],
                ['Future run rate', fmt$(app.future_run_rate)],
              ] as [string, string][]).map(([label, value]) => (
                <div key={label} style={{ background: '#f8fafc', border: '1px solid #eceff1', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#37474f' }}>{value}</div>
                </div>
              ))}
            </div>

            {pinErr && <div style={{ fontSize: 12.5, color: '#c62828', marginBottom: 10 }}>{pinErr}</div>}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="rd-btn rd-btn-ghost" onClick={() => setView('full')} style={{ fontSize: 12 }}>
                Open full analysis →
              </button>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                {canPin && !isPinned && (
                  <button className="rd-btn rd-btn-info" onClick={doPin} disabled={pinBusy} style={{ fontSize: 12 }}
                    title="Make this the application's current analysis">
                    📌 Pin as current
                  </button>
                )}
                {canPin && isPinned && (
                  <button className="rd-btn rd-btn-ghost" onClick={doUnpin} disabled={pinBusy} style={{ fontSize: 12 }}
                    title="Stop pinning — fall back to the latest analysis">
                    Unpin (use latest)
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Drawer body — the full analysis */}
        {view === 'full' && (
        <div style={{ padding: '16px 20px', flex: 1 }}>
          {/* Badges row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {app.excluded_from_apr ? (
              <span className="rd-pill" style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }}>
                Not relevant for disposition
              </span>
            ) : (
              <span className={dispositionPillClass(app.disposition)}>{app.disposition ? dispLabel(app.disposition) : 'No disposition'}</span>
            )}
            {!app.excluded_from_apr && app.confidence && <span className={confidencePillClass(app.confidence)}>{app.confidence}</span>}
            {app.overridden && (
              <span className="rd-pill" style={{ background: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082' }}>
                Overridden
              </span>
            )}
          </div>

          {/* Not relevant for disposition banner */}
          {app.excluded_from_apr && (
            <div style={{
              padding: '10px 14px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              marginBottom: 16,
              fontSize: 13,
              color: '#475569',
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}>
              <i className="material-icons" style={{ fontSize: 18, flexShrink: 0, marginTop: 1, color: '#94a3b8' }}>block</i>
              <span>This application is marked <strong>“Not relevant for rationalization”</strong> in Application Master Data (e.g. a foundational platform), so no disposition was produced for it.</span>
            </div>
          )}

          {/* Needs review banner */}
          {app.confidence === 'needs_review' && (
            <div style={{
              padding: '10px 14px',
              background: '#fff3e0',
              border: '1px solid #ffcc02',
              borderRadius: 6,
              marginBottom: 16,
              fontSize: 13,
              color: '#e65100',
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}>
              <i className="material-icons" style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>tips_and_updates</i>
              <span>Add more knowledge files for this application to improve the recommendation.</span>
            </div>
          )}

          {/* Overridden: show original */}
          {app.overridden && app.original_disposition && (
            <div style={{ fontSize: 12, color: '#9e9e9e', marginBottom: 12 }}>
              Original disposition: <strong>{app.original_disposition}</strong>
            </div>
          )}

          {/* Rationale */}
          {app.rationale && (
            <section style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Rationale</div>
              <p style={{ fontSize: 14, color: '#424242', lineHeight: 1.7, margin: 0 }}>{app.rationale}</p>
            </section>
          )}

          {/* Full justification */}
          {app.disposition_justification && (
            <section style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Justification</div>
              <p style={{ fontSize: 14, color: '#424242', lineHeight: 1.7, margin: 0 }}>{app.disposition_justification}</p>
            </section>
          )}

          {/* Strategic options — primary path vs credible alternatives */}
          {app.strategic_options && (
            <section style={{ marginBottom: 16, background: '#f5f7ff', border: '1px solid #dbe3ff', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Strategic options</div>
              <div style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.6 }}>{renderMd(app.strategic_options)}</div>
            </section>
          )}

          {/* Override note */}
          {app.override_note && (
            <section style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#f57f17', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Override note</div>
              <p style={{ fontSize: 14, color: '#424242', lineHeight: 1.7, margin: 0 }}>{app.override_note}</p>
            </section>
          )}

          {/* Savings */}
          <section style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Savings range</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {[
                ['Low', app.savings_low],
                ['Mid', app.savings_mid],
                ['High', app.savings_high],
              ].map(([label, val]) => (
                <div key={label as string} style={{ background: '#f5f5f5', borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label as string}</div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#2e7d32', marginTop: 2 }}>{fmt$(val as number)}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Costs */}
          <section style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Cost details</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {[
                  ['Current annual cost', fmt$(app.current_annual_cost)],
                  ['Disposition cost', fmtCostRange(app.disposition_cost, app.disposition_cost_low, app.disposition_cost_high)],
                  ['Implementation / succession cost', fmtCostRange(app.integration_cost, app.integration_cost_low, app.integration_cost_high)],
                  ['Total one-time cost', fmtCostRange(app.total_one_time_cost, app.total_one_time_cost_low, app.total_one_time_cost_high)],
                  ['Future run rate', fmt$(app.future_run_rate)],
                ].map(([label, val]) => (
                  <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '6px 0', color: '#666', verticalAlign: 'top' }}>{label}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600, color: '#212121' }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Program timeline (directional) */}
          {app.disposition_timeline && (
            <section style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Program timeline <span style={{ fontWeight: 400, textTransform: 'none', color: '#9e9e9e' }}>· directional</span></div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#eef7ff', border: '1px solid #cfe4ff', borderRadius: 8, padding: '10px 12px' }}>
                <i className="material-icons" style={{ fontSize: 18, color: '#1976d2', flexShrink: 0 }}>schedule</i>
                <span style={{ fontSize: 13, color: '#1d3a5f', lineHeight: 1.5 }}>{app.disposition_timeline}</span>
              </div>
            </section>
          )}

          {/* Future run rate justification */}
          {app.future_run_rate_justification && (
            <section style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Run rate justification</div>
              <p style={{ fontSize: 13, color: '#424242', lineHeight: 1.6, margin: 0 }}>{app.future_run_rate_justification}</p>
            </section>
          )}

          {/* Methodology note */}
          {app.methodology_note && (
            <section style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Methodology note</div>
              <p style={{ fontSize: 13, color: '#424242', lineHeight: 1.6, margin: 0 }}>{app.methodology_note}</p>
            </section>
          )}

          {/* Override form or button */}
          {!showOverride && (
            <button
              className="rd-btn rd-btn-warning"
              style={{ marginTop: 4 }}
              onClick={() => setShowOverride(true)}
            >
              <i className="material-icons">edit</i>
              Override recommendation
            </button>
          )}
          {showOverride && (
            <OverrideForm
              app={app}
              runId={runId}
              onSaved={handleSaved}
              onCancel={() => setShowOverride(false)}
            />
          )}
        </div>
        )}
      </div>
    </>
  );
}

/* ---- Tab: Executive Summary ---- */
function ExecSummaryTab({ run, onViewApps }: { run: AgentRunDetail; onViewApps: () => void }) {
  const es = run.execSummary;
  const analyzedCount = run.apps.filter((a: AgentRunApp) => a.status === 'completed').length;

  return (
    <div>
      {/* Stat cards */}
      <div className="rd-stat-grid" style={{ marginBottom: 20 }}>
        {[
          { icon: 'attach_money', color: 'blue',   label: 'Total In-Scope Cost',   value: fmt$(es?.total_in_scope_cost) },
          { icon: 'trending_down', color: 'green',  label: 'Total Savings (Mid)',   value: fmt$(es?.total_savings_mid) },
          { icon: 'payments',      color: 'orange', label: 'Total One-Time Cost',   value: fmt$(es?.total_one_time_cost) },
          { icon: 'apps',          color: 'purple', label: 'Apps Analyzed',         value: fmtNum(analyzedCount), onClick: onViewApps },
        ].map(s => (
          <div
            key={s.label}
            className="rd-stat-card"
            onClick={s.onClick}
            style={s.onClick ? { cursor: 'pointer' } : undefined}
            title={s.onClick ? 'Open the Applications tab to see each application analysis' : undefined}
          >
            <div className={`rd-stat-card-icon ${s.color}`}>
              <i className="material-icons">{s.icon}</i>
            </div>
            <div className="rd-stat-card-body">
              <div className="rd-stat-card-value">{s.value}</div>
              <div className="rd-stat-card-label">{s.label}{s.onClick ? ' ›' : ''}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Savings range */}
      {(es?.total_savings_low != null || es?.total_savings_high != null) && (
        <div className="rd-card" style={{ marginBottom: 16, padding: '12px 16px' }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Total Savings Range</div>
          <div style={{ display: 'flex', gap: 24 }}>
            {[['Low', es?.total_savings_low], ['Mid', es?.total_savings_mid], ['High', es?.total_savings_high]].map(([lbl, val]) => (
              <div key={lbl as string}>
                <div style={{ fontSize: 11, color: '#9e9e9e', marginBottom: 2 }}>{lbl as string}</div>
                <div style={{ fontWeight: 700, fontSize: 18, color: '#2e7d32' }}>{fmt$(val as number)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Headline */}
      {es?.headline_md && (
        <div className="rd-card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Headline</div>
          <div style={{ fontSize: 14, color: '#424242' }}>{renderMd(es.headline_md)}</div>
        </div>
      )}

      {/* Body */}
      {es?.body_md && (
        <div className="rd-card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Summary</div>
          <div style={{ fontSize: 14, color: '#424242', lineHeight: 1.7 }}>{renderMd(es.body_md)}</div>
        </div>
      )}

      {!es?.headline_md && !es?.body_md && (
        <div className="rd-empty">
          <div className="rd-empty-icon"><i className="material-icons" style={{ fontSize: 36 }}>article</i></div>
          <p className="rd-empty-body">Executive summary not yet available.</p>
        </div>
      )}
    </div>
  );
}

/* ---- Tab: Applications ---- */
interface AppsTabProps {
  apps: AgentRunApp[];
  runId: string;
  onUpdated: (id: string, patch: Partial<AgentRunApp>) => void;
  focusAppId?: string;
  onFocusHandled?: () => void;
}

function AppsTab({ apps, runId, onUpdated, focusAppId, onFocusHandled }: AppsTabProps) {
  const [search, setSearch] = useState('');
  const [dispFilter, setDispFilter] = useState<string[]>([]);
  const [confFilter, setConfFilter] = useState('');
  const [selectedApp, setSelectedApp] = useState<AgentRunApp | null>(null);

  // A focus request is a ONE-SHOT: open the drawer, then tell the parent it's been
  // handled so focusAppId clears. Without that, this effect re-fires on every `apps`
  // identity change (the 3s status poll replaces the run) and re-opens the drawer
  // the user just closed.
  useEffect(() => {
    if (!focusAppId) return;
    const found = apps.find(a => a.application_id === focusAppId || a.id === focusAppId);
    if (found) {
      setSelectedApp(found);
      onFocusHandled?.();
    }
  }, [focusAppId, apps, onFocusHandled]);

  const allDispositions = Array.from(new Set(apps.map(a => a.disposition).filter(Boolean))) as string[];

  const filtered = apps.filter(app => {
    const matchSearch = !search ||
      app.application_name.toLowerCase().includes(search.toLowerCase()) ||
      (app.application_id_code || '').toLowerCase().includes(search.toLowerCase()) ||
      (app.vendor || '').toLowerCase().includes(search.toLowerCase());
    const matchDisp = !dispFilter.length || dispFilter.includes(app.disposition || '');
    const matchConf = !confFilter || app.confidence === confFilter;
    return matchSearch && matchDisp && matchConf;
  });

  const toggleDisp = (d: string) => {
    setDispFilter(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const handleRowClick = (app: AgentRunApp) => setSelectedApp(app);

  const handleDrawerUpdate = (id: string, patch: Partial<AgentRunApp>) => {
    onUpdated(id, patch);
    // Update selected app in drawer too
    setSelectedApp((prev: AgentRunApp | null) => prev && prev.id === id ? { ...prev, ...patch } : prev);
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="rd-filters-bar" style={{ marginBottom: 12 }}>
        <input
          className="rd-input"
          type="search"
          placeholder="Search app or vendor…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="rd-select"
          value={confFilter}
          onChange={e => setConfFilter(e.target.value)}
        >
          <option value="">All confidence</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="needs_review">Needs review</option>
        </select>
      </div>

      {/* Disposition pill filters */}
      {allDispositions.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {allDispositions.map(d => (
            <button
              key={d}
              onClick={() => toggleDisp(d)}
              style={{
                cursor: 'pointer',
                border: 'none',
                padding: 0,
                background: 'none',
              }}
            >
              <span className={dispositionPillClass(d)} style={{
                cursor: 'pointer',
                opacity: dispFilter.length === 0 || dispFilter.includes(d) ? 1 : 0.4,
                border: dispFilter.includes(d) ? '2px solid #1565c0' : '2px solid transparent',
                boxSizing: 'border-box',
              }}>
                {d}
              </span>
            </button>
          ))}
          {dispFilter.length > 0 && (
            <button className="rd-btn rd-btn-ghost" style={{ height: 26, padding: '0 8px', fontSize: 11 }} onClick={() => setDispFilter([])}>
              Clear
            </button>
          )}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="rd-table">
          <thead>
            <tr>
              <th>App ID</th>
              <th>Vendor</th>
              <th>Application</th>
              <th>Disposition</th>
              <th>Confidence</th>
              <th style={{ textAlign: 'right' }}>Current $</th>
              <th style={{ textAlign: 'right' }}>Savings Mid</th>
              <th style={{ textAlign: 'right' }}>Future Run Rate</th>
              <th>Source</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', color: '#9e9e9e', padding: 32 }}>
                  No applications match the current filters.
                </td>
              </tr>
            )}
            {filtered.map(app => {
              const isSelected = selectedApp?.id === app.id;
              return (
              <tr
                key={app.id}
                onClick={() => handleRowClick(app)}
                style={{
                  cursor: 'pointer',
                  // Selected row (its detail panel is open) wins over the needs-review tint.
                  background: isSelected ? '#e3f2fd' : app.confidence === 'needs_review' ? '#fff8e1' : undefined,
                  boxShadow: isSelected ? 'inset 3px 0 0 #1565c0' : undefined,
                }}
              >
                <td style={{ fontWeight: 600, color: '#212121', whiteSpace: 'nowrap' }}>{app.application_id_code || '—'}</td>
                <td className="rd-cell-muted">{app.vendor || '—'}</td>
                <td>
                  <span style={{ fontWeight: 500, color: '#1565c0' }}>{app.application_name}</span>
                  {app.overridden && (
                    <span style={{ marginLeft: 6, fontSize: 10, background: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082', borderRadius: 10, padding: '1px 6px' }}>
                      Overridden
                    </span>
                  )}
                </td>
                <td>
                  {app.excluded_from_apr ? (
                    <span
                      title="Marked 'Not relevant for rationalization' in Application Master Data"
                      style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}
                    >
                      Not relevant for disposition
                    </span>
                  ) : (
                    <span className={dispositionPillClass(app.disposition)}>
                      {dispLabel(app.disposition)}
                    </span>
                  )}
                </td>
                <td>
                  {app.excluded_from_apr
                    ? <span className="rd-cell-muted">—</span>
                    : app.confidence
                      ? <span className={confidencePillClass(app.confidence)}>{app.confidence}</span>
                      : <span className="rd-cell-muted">—</span>
                  }
                </td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{app.excluded_from_apr ? '—' : fmt$(app.current_annual_cost)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace', color: '#2e7d32', fontWeight: 600 }}>{app.excluded_from_apr ? '—' : fmt$(app.savings_mid)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{app.excluded_from_apr ? '—' : fmt$(app.future_run_rate)}</td>
                <td><OriginBadge origin={app.origin} /></td>
                <td>
                  <span className={
                    app.status === 'completed' ? 'rd-pill rd-pill-success' :
                    app.status === 'failed' ? 'rd-pill rd-pill-danger' :
                    app.status === 'analyzing' ? 'rd-pill rd-pill-info' :
                    'rd-pill rd-pill-neutral'
                  }>{app.status}</span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Drawer */}
      {selectedApp && (
        <AppDrawer
          key={selectedApp.id}
          app={selectedApp}
          runId={runId}
          onClose={() => setSelectedApp(null)}
          onUpdated={handleDrawerUpdate}
        />
      )}
    </div>
  );
}

/* ---- Tab: Themes ---- */
function ThemesTab({ themes, apps, onAppClick }: {
  themes: AgentRunTheme[];
  apps: AgentRunApp[];
  onAppClick: (appId: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const sorted = [...themes].sort((a, b) => a.sort_order - b.sort_order);

  if (!sorted.length) {
    return (
      <div className="rd-empty">
        <div className="rd-empty-icon"><i className="material-icons" style={{ fontSize: 36 }}>category</i></div>
        <p className="rd-empty-body">No themes available for this run.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {sorted.map(theme => {
        const isOpen = expanded.has(theme.id);
        const themeApps = apps.filter(a => theme.application_ids.includes(a.application_id || a.id));
        return (
          <div key={theme.id} className="rd-card" style={{ padding: 0, overflow: 'hidden' }}>
            <button
              onClick={() => toggle(theme.id)}
              style={{
                width: '100%', textAlign: 'left', border: 'none',
                background: 'none', cursor: 'pointer',
                padding: '14px 16px',
                display: 'flex', alignItems: 'flex-start', gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#212121' }}>{theme.title}</span>
                  <span className="rd-pill rd-pill-neutral" style={{ fontSize: 11 }}>{theme.theme_type}</span>
                  <span className="rd-pill rd-pill-neutral" style={{ fontSize: 11 }}>{themeApps.length} app{themeApps.length !== 1 ? 's' : ''}</span>
                </div>
                {theme.summary && (
                  <p style={{ fontSize: 13, color: '#666', margin: '4px 0 0', lineHeight: 1.5 }}>{theme.summary}</p>
                )}
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                {(theme.estimated_savings_low != null || theme.estimated_savings_mid != null) && (
                  <div style={{ fontSize: 13, color: '#2e7d32', fontWeight: 600 }}>
                    {fmt$(theme.estimated_savings_mid)} mid savings
                  </div>
                )}
                <i className="material-icons" style={{ fontSize: 20, color: '#9e9e9e', marginTop: 4, display: 'block' }}>
                  {isOpen ? 'expand_less' : 'expand_more'}
                </i>
              </div>
            </button>

            {isOpen && (
              <div style={{ borderTop: '1px solid #e0e0e0', padding: '14px 16px', background: '#fafafa' }}>
                {/* Savings range */}
                {(theme.estimated_savings_low != null || theme.estimated_savings_high != null) && (
                  <div style={{ display: 'flex', gap: 20, marginBottom: 14 }}>
                    {[['Low', theme.estimated_savings_low], ['Mid', theme.estimated_savings_mid], ['High', theme.estimated_savings_high]].map(([lbl, val]) => (
                      <div key={lbl as string}>
                        <div style={{ fontSize: 11, color: '#9e9e9e' }}>{lbl as string}</div>
                        <div style={{ fontWeight: 700, color: '#2e7d32' }}>{fmt$(val as number)}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Details markdown */}
                {theme.details_md && (
                  <div style={{ fontSize: 14, color: '#424242', marginBottom: 14 }}>
                    {renderMd(theme.details_md)}
                  </div>
                )}

                {/* Involved apps */}
                {themeApps.length > 0 && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                      Involved applications
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {themeApps.map(a => (
                        <button
                          key={a.id}
                          onClick={() => onAppClick(a.application_id || a.id)}
                          className="rd-btn rd-btn-ghost"
                          style={{ height: 28, padding: '0 10px', fontSize: 12 }}
                        >
                          {a.application_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---- Tab: Action Plan ---- */
function ActionPlanTab({ items }: { items: AgentRunDetail['actionPlan'] }) {
  const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
  const sections = Array.from(new Set(sorted.map(i => i.section || 'General')));

  if (!sorted.length) {
    return (
      <div className="rd-empty">
        <div className="rd-empty-icon"><i className="material-icons" style={{ fontSize: 36 }}>checklist</i></div>
        <p className="rd-empty-body">No action plan items available.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {sections.map(section => {
        const sectionItems = sorted.filter(i => (i.section || 'General') === section);
        return (
          <div key={section}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #e3f2fd' }}>
              {section}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sectionItems.map(item => (
                <div key={item.id} className="rd-card" style={{ padding: '12px 16px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {(item.vendor || item.application_id) && (
                      <div style={{ fontSize: 12, color: '#9e9e9e', marginBottom: 4 }}>
                        {item.vendor}{item.vendor && item.application_id ? ' · ' : ''}{item.application_id}
                      </div>
                    )}
                    <div style={{ fontSize: 14, color: '#212121', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {item.action_text}
                    </div>
                  </div>
                  {(item.savings_low != null || item.savings_mid != null || item.savings_high != null) && (
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#9e9e9e' }}>Savings range</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#2e7d32' }}>
                        {fmt$(item.savings_low)} – {fmt$(item.savings_high)}
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>Mid: {fmt$(item.savings_mid)}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---- Tab: Methodology ---- */
// The instructions that drove the analysis, shown on the Methodology tab so every
// recommendation is auditable.
function AnalysisBasis({ run }: { run: AgentRunDetail }) {
  const instr = (run.additional_comments || '').trim();
  if (!instr) return null;
  return (
    <div className="rd-card" style={{ marginBottom: 16, borderLeft: '4px solid #1565c0' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        Analysis basis — inputs behind this analysis
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#607d8b', marginBottom: 4 }}>Additional Analysis Instructions</div>
        <div style={{ fontSize: 13.5, color: '#37474f', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{instr}</div>
      </div>
    </div>
  );
}

function MethodologyTab({ run }: { run: AgentRunDetail }) {
  const md = run.execSummary?.methodology_md;
  return (
    <div>
      <AnalysisBasis run={run} />
      {md ? (
        <div className="rd-card" style={{ fontSize: 14, color: '#424242', lineHeight: 1.7 }}>
          {renderMd(md)}
        </div>
      ) : (
        <div className="rd-empty">
          <div className="rd-empty-icon"><i className="material-icons" style={{ fontSize: 36 }}>science</i></div>
          <p className="rd-empty-body">Methodology notes not available for this run.</p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Helpers for export / import                                          */
/* ------------------------------------------------------------------ */

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
}

function humanizeField(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const LONG_TEXT_FIELDS = new Set([
  'disposition_justification',
  'rationale',
  'methodology_note',
  'future_run_rate_justification',
  'override_note',
]);

/* ------------------------------------------------------------------ */
/* Import Modal                                                          */
/* ------------------------------------------------------------------ */

type ImportStep = 'pick' | 'loading' | 'preview' | 'applied';

interface ImportModalProps {
  runId: string;
  onClose: () => void;
  onApplied: () => void;
}

function ImportModal({ runId, onClose, onApplied }: ImportModalProps) {
  const [step, setStep] = useState<ImportStep>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [note, setNote] = useState('');
  const [applyError, setApplyError] = useState('');
  const [parseError, setParseError] = useState('');
  const [applying, setApplying] = useState(false);
  const [appliedResult, setAppliedResult] = useState<{ applied: number; skipped: number } | null>(null);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const toggleExpand = (key: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParseError('');
  };

  const handleNext = async () => {
    if (!file) return;
    setStep('loading');
    setParseError('');
    try {
      const result = await previewImport(runId, file);
      setPreview(result);
      setStep('preview');
    } catch {
      setParseError("Couldn't read the file — please make sure it's the Excel file you downloaded from this run.");
      setStep('pick');
    }
  };

  const handleApply = async () => {
    if (!preview || note.trim().length < 3) return;
    setApplying(true);
    setApplyError('');
    try {
      const result = await applyImport(runId, preview, note.trim());
      setAppliedResult({ applied: result.applied, skipped: result.skipped });
      setStep('applied');
    } catch {
      setApplyError('Failed to apply changes. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    if (step === 'applied') {
      onApplied();
    }
    onClose();
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  };

  const modalStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: 10,
    width: '100%',
    maxWidth: 680,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  };

  const modalHeaderStyle: React.CSSProperties = {
    padding: '18px 24px 14px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const modalBodyStyle: React.CSSProperties = {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1,
  };

  const modalFooterStyle: React.CSSProperties = {
    padding: '14px 24px',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={modalHeaderStyle}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#212121' }}>
            {step === 'applied' ? 'Import complete' : 'Import edits from Excel'}
          </div>
          <button className="rd-icon-btn" onClick={handleClose} aria-label="Close">
            <i className="material-icons">close</i>
          </button>
        </div>

        {/* Step: pick file */}
        {step === 'pick' && (
          <>
            <div style={modalBodyStyle}>
              <p style={{ fontSize: 14, color: '#424242', lineHeight: 1.6, marginBottom: 20 }}>
                Download the Excel export, edit any dispositions, justifications, or savings values in Excel,
                save the file, then upload it here. We'll show you a diff before applying.
              </p>
              <label style={{ display: 'block' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#424242', display: 'block', marginBottom: 6 }}>
                  Select Excel file (.xlsx)
                </span>
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    border: '2px dashed #e0e0e0',
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: 'pointer',
                    background: '#fafafa',
                    boxSizing: 'border-box',
                  }}
                />
              </label>
              {file && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#424242', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="material-icons" style={{ fontSize: 18, color: '#2e7d32' }}>check_circle</i>
                  {file.name}
                </div>
              )}
              {parseError && (
                <div style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  background: '#ffebee',
                  border: '1px solid #ffcdd2',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#c62828',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                }}>
                  <i className="material-icons" style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>error</i>
                  {parseError}
                </div>
              )}
            </div>
            <div style={modalFooterStyle}>
              <button className="rd-btn rd-btn-ghost" onClick={handleClose}>Cancel</button>
              <button
                className="rd-btn rd-btn-primary"
                onClick={handleNext}
                disabled={!file}
              >
                Next &rarr;
              </button>
            </div>
          </>
        )}

        {/* Step: loading */}
        {step === 'loading' && (
          <div style={{ ...modalBodyStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 16 }}>
            <div style={{
              width: 40, height: 40,
              border: '3px solid #e3f2fd',
              borderTop: '3px solid #1565c0',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <p style={{ fontSize: 14, color: '#666', margin: 0 }}>Parsing your edits&hellip;</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Step: diff preview */}
        {step === 'preview' && preview && (
          <>
            <div style={modalBodyStyle}>
              {/* Summary bar */}
              <div style={{
                padding: '10px 14px',
                background: '#e3f2fd',
                borderRadius: 6,
                marginBottom: 16,
                fontSize: 13,
                color: '#1565c0',
                fontWeight: 500,
              }}>
                {preview.summary.appsWithChanges} application{preview.summary.appsWithChanges !== 1 ? 's' : ''}&ensp;&middot;&ensp;
                {preview.summary.totalFieldChanges} change{preview.summary.totalFieldChanges !== 1 ? 's' : ''}&ensp;
                ({preview.summary.dispositionChanges > 0 && `${preview.summary.dispositionChanges} disposition · `}
                {preview.summary.savingsChanges > 0 && `${preview.summary.savingsChanges} savings · `}
                {preview.summary.justificationChanges > 0 && `${preview.summary.justificationChanges} justification · `}
                {preview.summary.otherChanges > 0 && `${preview.summary.otherChanges} other`})
              </div>

              {/* Warnings */}
              {preview.warnings.length > 0 && (
                <div style={{
                  marginBottom: 16,
                  padding: '10px 14px',
                  background: '#fff8e1',
                  border: '1px solid #ffe082',
                  borderRadius: 6,
                }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#f57f17', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="material-icons" style={{ fontSize: 16 }}>warning</i>
                    Warnings
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {preview.warnings.map((w, i) => (
                      <li key={i} style={{ fontSize: 13, color: '#e65100', marginBottom: 4, lineHeight: 1.5 }}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Diffs by application */}
              {preview.diffs.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9e9e9e', padding: '24px 0', fontSize: 14 }}>
                  No changes detected between the uploaded file and the current run data.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {preview.diffs.map((diff: ImportDiff) => (
                    <div
                      key={diff.appRunId}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        overflow: 'hidden',
                        borderLeft: diff.changes.some(c => c.field === 'disposition') ? '4px solid #1565c0' : '4px solid #e0e0e0',
                      }}
                    >
                      {/* Card header */}
                      <div style={{
                        padding: '10px 14px',
                        background: '#f5f5f5',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#212121',
                      }}>
                        {diff.applicationName}
                      </div>

                      {/* Changes table */}
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#fafafa' }}>
                            <th style={{ padding: '6px 14px', textAlign: 'left', fontWeight: 600, color: '#666', borderBottom: '1px solid #f0f0f0', width: '25%' }}>Field</th>
                            <th style={{ padding: '6px 14px', textAlign: 'left', fontWeight: 600, color: '#666', borderBottom: '1px solid #f0f0f0', width: '37.5%' }}>Before</th>
                            <th style={{ padding: '6px 14px', textAlign: 'left', fontWeight: 600, color: '#666', borderBottom: '1px solid #f0f0f0', width: '37.5%' }}>After</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diff.changes.map((change, ci) => {
                            const isLong = LONG_TEXT_FIELDS.has(change.field);
                            const expandKey = `${diff.appRunId}-${change.field}`;
                            const isExpanded = expandedFields.has(expandKey);
                            const isNumeric = typeof change.before === 'number' || typeof change.after === 'number';
                            const isDisposition = change.field === 'disposition';
                            const beforeStr = change.before != null ? String(change.before) : '—';
                            const afterStr = change.after != null ? String(change.after) : '—';

                            return (
                              <tr key={ci} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                <td style={{ padding: '8px 14px', verticalAlign: 'top', color: '#424242', fontWeight: isDisposition ? 600 : 400 }}>
                                  {humanizeField(change.field)}
                                </td>
                                <td style={{ padding: '8px 14px', verticalAlign: 'top' }}>
                                  {isLong ? (
                                    <div>
                                      <span style={{ color: '#9e9e9e', fontSize: 13 }}>
                                        {isExpanded ? beforeStr : (beforeStr.length > 80 ? beforeStr.slice(0, 80) + '…' : beforeStr)}
                                      </span>
                                      {beforeStr.length > 80 && (
                                        <button
                                          onClick={() => toggleExpand(expandKey)}
                                          style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#1565c0', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                        >
                                          {isExpanded ? 'Show less' : 'View full'}
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <span style={{
                                      color: '#9e9e9e',
                                      fontFamily: isNumeric ? 'monospace' : 'inherit',
                                      textDecoration: typeof change.before === 'string' ? 'line-through' : undefined,
                                    }}>
                                      {beforeStr}
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: '8px 14px', verticalAlign: 'top' }}>
                                  {isLong ? (
                                    <div>
                                      <span style={{ color: '#1565c0', fontSize: 13, fontWeight: 500 }}>
                                        {isExpanded ? afterStr : (afterStr.length > 80 ? afterStr.slice(0, 80) + '…' : afterStr)}
                                      </span>
                                    </div>
                                  ) : (
                                    <span style={{
                                      color: '#1565c0',
                                      fontWeight: 600,
                                      fontFamily: isNumeric ? 'monospace' : 'inherit',
                                    }}>
                                      {afterStr}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}

              {/* Override note */}
              <div style={{ marginTop: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#424242', marginBottom: 6 }}>
                  Override note (audit trail) <span style={{ color: '#c62828' }}>*</span>
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe why you are applying these changes (minimum 3 characters)…"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: 6,
                    fontSize: 13,
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {applyError && (
                <div style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  background: '#ffebee',
                  border: '1px solid #ffcdd2',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#c62828',
                }}>
                  {applyError}
                </div>
              )}
            </div>
            <div style={modalFooterStyle}>
              <button className="rd-btn rd-btn-ghost" onClick={() => setStep('pick')}>
                &larr; Back
              </button>
              <button
                className="rd-btn rd-btn-primary"
                onClick={handleApply}
                disabled={applying || note.trim().length < 3 || preview.diffs.length === 0}
              >
                {applying
                  ? 'Applying…'
                  : `Apply ${preview.summary.totalFieldChanges} change${preview.summary.totalFieldChanges !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}

        {/* Step: applied */}
        {step === 'applied' && appliedResult && (
          <>
            <div style={{ ...modalBodyStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 16, textAlign: 'center' }}>
              <i className="material-icons" style={{ fontSize: 48, color: '#2e7d32' }}>check_circle</i>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#212121' }}>
                Changes applied: {appliedResult.applied}
              </div>
              <p style={{ fontSize: 14, color: '#424242', margin: 0, lineHeight: 1.6, maxWidth: 400 }}>
                The disposition model has been updated for the affected applications.
                {appliedResult.skipped > 0 && ` (${appliedResult.skipped} row${appliedResult.skipped !== 1 ? 's' : ''} skipped — no changes detected.)`}
              </p>
            </div>
            <div style={modalFooterStyle}>
              <button className="rd-btn rd-btn-primary" onClick={handleClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Run list (no runId in URL)                                           */
/* ------------------------------------------------------------------ */

interface RunListItem {
  id: string;
  name: string;
  status: string;
  created_at: string;
  scope?: string;
  origin?: string;
  created_by_name?: string | null;
}

function RunList({ onSelect }: { onSelect: (id: string) => void }) {
  const [runs, setRuns] = useState<RunListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [resumingIds, setResumingIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const { hasPermission } = useAuth();
  const canImport = hasPermission('canRunAPRAnalysis');

  const IN_PROGRESS = ['queued', 'planning', 'running', 'synthesizing'];
  const refresh = () => listRuns().then((data: RunListItem[]) => { setRuns(data); setLoading(false); }).catch(() => { setError('Failed to load runs.'); setLoading(false); });

  useEffect(() => { refresh(); }, []);

  // While any run is still in progress, poll so statuses (and the Continue button) stay live.
  useEffect(() => {
    const anyActive = runs.some(r => IN_PROGRESS.includes(r.status));
    if (!anyActive) return;
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, [runs]);

  const importBtn = canImport ? (
    <button className="rd-btn rd-btn-secondary" onClick={() => setShowImport(true)}>
      <i className="material-icons" style={{ fontSize: 18 }}>upload_file</i>Import client analysis
    </button>
  ) : null;

  const modal = showImport ? (
    <ClientAnalysisImportModal onClose={() => setShowImport(false)} onDone={(id) => { setShowImport(false); onSelect(id); }} />
  ) : null;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9e9e9e' }}>Loading runs…</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#c62828' }}>{error}</div>;
  if (!runs.length) {
    return (
      <div className="rd-empty" style={{ padding: '48px 20px' }}>
        <div className="rd-empty-icon"><i className="material-icons" style={{ fontSize: 48 }}>insights</i></div>
        <h2 className="rd-empty-title">No analysis runs yet</h2>
        <p className="rd-empty-body">Launch an AI analysis from Run Analysis, or import a client-provided analysis document.</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
          <a href="/apr/application-evaluation/disposition-intelligence/run-analysis" className="rd-btn rd-btn-primary" style={{ textDecoration: 'none' }}>
            <i className="material-icons">auto_awesome</i>Go to Run Analysis
          </a>
          {importBtn}
        </div>
        {modal}
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#212121' }}>Select a run to view results</h2>
        {importBtn}
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="rd-input" type="search" placeholder="Search by run name or author…" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 260 }} />
        <input className="rd-input" type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} title="Filter by created date" />
        {(search || dateFilter) && (
          <button className="rd-btn rd-btn-ghost" onClick={() => { setSearch(''); setDateFilter(''); }}>Clear</button>
        )}
      </div>
      <table className="rd-table">
        <thead>
          <tr>
            <th>Run name</th>
            <th>Author</th>
            <th>Source</th>
            <th>Scope</th>
            <th>Status</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {runs.filter(run => {
            const s = search.trim().toLowerCase();
            const matchText = !s || run.name.toLowerCase().includes(s) || (run.created_by_name || '').toLowerCase().includes(s);
            const matchDate = !dateFilter || (!!run.created_at && new Date(run.created_at).toISOString().slice(0, 10) === dateFilter);
            return matchText && matchDate;
          }).map(run => (
            <tr key={run.id}>
              <td style={{ fontWeight: 500 }}>{run.name}</td>
              <td className="rd-cell-muted">{run.created_by_name || '—'}</td>
              <td><OriginBadge origin={run.origin} /></td>
              <td className="rd-cell-muted">{run.scope || '—'}</td>
              <td>
                <span className={
                  run.status === 'completed' ? 'rd-pill rd-pill-success' :
                  run.status === 'failed' ? 'rd-pill rd-pill-danger' :
                  run.status === 'cancelled' ? 'rd-pill rd-pill-neutral' :
                  'rd-pill rd-pill-info'
                }>{run.status}</span>
              </td>
              <td className="rd-cell-muted">{new Date(run.created_at).toLocaleDateString()}</td>
              <td style={{ display: 'flex', gap: 6 }}>
                {run.origin === 'client_document' && run.status !== 'completed' && run.status !== 'cancelled' && (
                  (resumingIds.has(run.id) || IN_PROGRESS.includes(run.status)) ? (
                    <button className="rd-btn rd-btn-ghost" disabled title="This run is being continued…">
                      Resuming…
                    </button>
                  ) : (
                    <button className="rd-btn rd-btn-warning"
                      title="This run was interrupted — continue it (skips work already done)"
                      onClick={async () => {
                        setResumingIds(prev => new Set(prev).add(run.id));
                        try { await resumeClientAnalysis(run.id); refresh(); }
                        catch (e: any) {
                          setResumingIds(prev => { const n = new Set(prev); n.delete(run.id); return n; });
                          alert(e?.response?.data?.error || 'Failed to resume');
                        }
                      }}>
                      Continue run
                    </button>
                  )
                )}
                <button className="rd-btn rd-btn-primary" onClick={() => onSelect(run.id)}>
                  View Results
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {modal}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                       */
/* ------------------------------------------------------------------ */

type TabKey = 'summary' | 'apps' | 'themes' | 'plan' | 'methodology';

const TABS: { key: TabKey; label: string; icon: string; desc: string }[] = [
  { key: 'summary',     label: 'Executive Summary', icon: 'summarize',
    desc: 'The board-level overview of the whole run: total in-scope spend, the projected annual savings range, total one-time cost, and the AI-written headline and narrative summary.' },
  { key: 'apps',        label: 'Applications',       icon: 'apps',
    desc: 'Every application analyzed, with its recommended disposition, confidence, current annual cost, savings and future run rate. Click a row to open that application’s full rationale and cost model.' },
  { key: 'themes',      label: 'Themes',             icon: 'category',
    desc: 'Cross-cutting opportunities the AI grouped across the whole portfolio rather than one app at a time — for example vendor-family renegotiation, consolidating overlapping tools, right-sizing over-provisioned licenses, and major-ERP modernization. Each theme spans several applications and carries its own estimated savings range.' },
  { key: 'plan',        label: 'Action Plan',        icon: 'checklist',
    desc: 'The prioritized list of concrete actions (renegotiate, right-size, consolidate, replace, retire) with the cost and savings attached to each — the execution roadmap that follows from the analysis.' },
  { key: 'methodology', label: 'Methodology',        icon: 'science',
    desc: 'How the numbers were derived — the savings benchmarks, one-time-cost heuristics, assumptions and scope the AI applied — so every recommendation is transparent and auditable.' },
];

const AnalysisResultsHelp: Help = {
  title: 'Analysis Results',
  icon: '📈',
  purpose: 'The output of a strategic analysis run — per-application dispositions with rationale, savings ranges, cross-cutting themes, a sequenced action plan, and the methodology behind it.',
  doHere: [
    'Pick a run (or open one from Run Analysis), then switch between the <b>Executive Summary</b>, <b>Applications</b>, <b>Themes</b>, <b>Action Plan</b>, and <b>Methodology</b> tabs.',
    'In <b>Applications</b>, search and filter by disposition or confidence, then click a row to open its detail drawer.',
    'Use <b>Override recommendation</b> in the drawer to change a disposition, justification, or savings range and record an audit note.',
    'Export the run as a <b>Word</b> report or a <b>PowerPoint</b> deck (convert to PDF yourself if needed).',
    'Use <b>Re-run</b> to launch a fresh analysis.',
    'Bring in the client’s own analysis with <b>Import client analysis</b> (Word/PPT/Excel): add instructions, choose <b>Document only</b> or <b>Document + portfolio consolidation scan</b>, and Claude interprets it into structured dispositions you can overwrite, consolidate, or keep.',
    'The <b>Source</b> column shows how each analysis was produced — <b>Baseline Assessment</b> (Claude), <b>Client</b>, <b>Merged</b>, or <b>Manual</b>.',
  ],
  fits: 'Application Evaluation &rarr; Disposition Intelligence &rarr; Analysis Results',
  firstTime: 'Open a completed run, skim the Executive Summary, then dive into the Applications tab to review each recommendation.',
  sections: [
    {
      type: 'defs',
      label: 'Dispositions & confidence',
      items: [
        { term: 'Retain', color: '#0d47a1', def: 'Keep the application as-is.' },
        { term: 'Renegotiate', color: '#9a3412', def: 'Keep but renegotiate contract or licensing terms.' },
        { term: 'Replace', color: '#7f1d1d', def: 'Replace the application with an alternative.' },
        { term: 'Consolidate', color: '#4a148c', def: 'Fold the application into another to remove overlap.' },
        { term: 'Needs review', color: '#9a3412', def: 'Low confidence — add more knowledge files to improve the recommendation.' },
      ],
    },
    {
      type: 'html',
      label: 'Two kinds of analysis',
      html: 'Every run is one of two kinds, shown in the <b>Source</b> column. <b>Baseline Assessment</b> — Claude analyses the portfolio from your master data and knowledge files. <b>Client / Merged</b> — the analysis is anchored to a document the client wrote (see below). <b>Manual</b> is reserved for hand-entered analysis. Both kinds use the same reasoning and produce the same tabs (Summary, Applications, Themes, Action Plan, Methodology).',
    },
    {
      type: 'html',
      label: 'Import client analysis — you’re in the driving seat',
      html: 'Click <b>Import client analysis</b> and pick or upload the client’s own document (Word / PPT / Excel). We detect the applications it mentions, expanding roll-up systems (e.g. “SAP ECC 6.0”) into their analyzable modules, then Claude interprets it into structured dispositions. The document is the <b>anchor</b>, but the depth is driven by <b>what you ask for</b> — the more specific your instructions and requirements (targets, constraints, systems to harmonize), the richer the result; vague input yields a thin one.',
    },
    {
      type: 'html',
      label: 'Scope & analysis instructions',
      html: 'Choose <b>Document only</b> to interpret just what the document covers, or <b>Document + portfolio consolidation scan</b> to also reason across <i>every</i> active application and surface harmonization — e.g. two SAP ECC instances (US 80% / EU 20%) consolidating onto one S/4HANA, or duplicate MuleSoft / Oracle / Dynamics estates onto one. Use <b>Additional Analysis Instructions</b> to state what the client is trying to achieve and anything you specifically want considered (revenue split across instances, process divergence, localization needs, customization depth, target dates, budget). <b>You are in the driving seat</b> — the more specific the instructions, the richer and more tailored the analysis; vague input yields a thin analysis.',
    },
    {
      type: 'html',
      label: 'How the analysis reasons (ERP playbook)',
      html: 'Both analyses apply a modernization playbook, directionally: <b>SAP ECC</b> (maintenance ends 2027) → <b>S/4HANA</b>, with the path (<b>Greenfield / Brownfield / Bluefield</b>) chosen from custom-object count, customization depth and system age. Multiple instances of one ERP consolidate around the higher-revenue / more-important instance — <i>unless</i> processes diverge materially, when each is upgraded separately (the client decides). An instance carrying statutory localization (France chart of accounts, Brazil Nota Fiscal) tends to remain foundational. <b>Oracle EBS → Fusion Cloud</b>. SaaS (<b>Salesforce, Dynamics 365</b>) is not “upgraded” — it is replaced/consolidated or kept to inherit the functions it does best.',
    },
    {
      type: 'html',
      label: 'Reconcile, version & pin',
      html: 'When a client analysis targets an app that already has one, choose per app: <b>Overwrite</b> (the client analysis becomes current; the prior drops to history), <b>Consolidate</b> (an AI merge reconciles client + existing, noting what changed, and pins the result), or <b>Keep as alternative</b> (stored unpinned). Every version is kept in the <b>Analysis Repository</b>, where you can compare history and pin the one that is current. At run time you can also <b>skip</b> apps that already have a client analysis so a new AI run inherits it.',
    },
    {
      type: 'html',
      label: 'Analysis basis (audit) & Insufficient Data',
      html: 'The <b>Methodology</b> tab shows the <b>Analysis basis</b> — the Additional Analysis Instructions that drove the run — so every recommendation is auditable. Where the client document does not cover an application or gives too little to assess, the disposition is recorded as <b>Insufficient Data</b> (rather than a fabricated call), with a note on what is missing.',
    },
    {
      type: 'html',
      label: 'Tip',
      html: 'Exports become available only once a run has <b>completed</b>. A client-analysis run is a full run — it produces themes, an executive summary, an action plan and methodology, not just per-app dispositions.',
    },
  ],
  links: [
    { href: '/apr/application-evaluation/disposition-intelligence/run-analysis', label: 'Run Analysis' },
    { href: '/apr/knowledge-library/architecture-process-diagrams', label: 'Knowledge Library' },
  ],
};

const AnalysisResults: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'Administrator';
  const [searchParams, setSearchParams] = useSearchParams();
  const runId = searchParams.get('runId');
  const appIdParam = searchParams.get('appId'); // deep-link from Application Master Data → this app's analysis

  const [deleting, setDeleting] = useState(false);

  const [run, setRun] = useState<AgentRunDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('summary');
  const [focusAppId, setFocusAppId] = useState<string | undefined>(undefined);

  // XLSX export state
  const [xlsxDownloading, setXlsxDownloading] = useState(false);

  // PPTX export state
  const [pptxDownloading, setPptxDownloading] = useState(false);

  // DOCX export state
  const [docxDownloading, setDocxDownloading] = useState(false);

  // Cached report deliverable for this run (download-only; generated in the
  // Report Library, not on this screen).
  const [runReport, setRunReport] = useState<CurrentReport>({ artifact: null, stale: false });

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  // Reached through a ref so loadRun can stay referentially STABLE. If loadRun
  // depended on setSearchParams directly, every URL change would give it a new
  // identity, re-firing the "load run on mount" effect below — an unwanted refetch
  // that unmounts the Applications tab and closes whatever drawer is open.
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => { setSearchParamsRef.current = setSearchParams; }, [setSearchParams]);

  const loadRun = useCallback(async (id: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await getRun(id);
      setRun(data);
    } catch {
      // Never surface an error banner — a missing/deleted run just means "no data
      // for this id". Drop the runId so we fall back to the run-list / empty state.
      setRun(null);
      setSearchParamsRef.current({}, { replace: true });
    } finally {
      setLoading(false);
    }
  }, []);

  // Start/stop polling when run status changes
  useEffect(() => {
    if (!run) return;
    if (TERMINAL.has(run.status)) { stopPolling(); return; }

    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const progress = await getProgress(run.id);
        if (TERMINAL.has(progress.status)) {
          stopPolling();
          // Re-fetch full run
          const full = await getRun(run.id);
          setRun(full);
        } else {
          setRun((prev: AgentRunDetail | null) => prev ? {
            ...prev,
            status: progress.status,
            progress_pct: progress.progress_pct,
            status_message: progress.status_message,
          } : prev);
        }
      } catch { /* ignore transient poll errors */ }
    }, 3000);

    return stopPolling;
  }, [run?.status, run?.id]);

  // Load run on mount / when runId changes.
  //
  // The URL is the single source of truth for which run is open, so when runId goes
  // away the loaded run MUST go with it. Without the else branch, browser Back off a
  // run left `run` populated while the URL said "list": the list renders on !runId and
  // the detail renders on `run`, so both appeared at once — the run detail sitting
  // under the list of runs.
  useEffect(() => {
    if (runId) { loadRun(runId); return; }
    setRun(null);
    setActiveTab('summary');
    setFocusAppId(undefined);
    setRunReport({ artifact: null, stale: false });
    setError('');
  }, [runId, loadRun]);

  // Deep-link ?appId=<id> (from Application Master Data / Analysis Repository) →
  // land on the Applications tab for that run. It does not open the app's detail
  // panel: panels on this screen open only when the user clicks a row, the same as
  // every other screen.
  //
  // The param is a one-shot and is consumed (dropped from the URL) so it can't re-fire
  // on a later render or survive a refresh. Consuming it is only safe because loadRun
  // is stable (see above); otherwise the URL change refetches and remounts the tab.
  const consumedAppIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (appIdParam && run && !loading && consumedAppIdRef.current !== appIdParam) {
      consumedAppIdRef.current = appIdParam;
      setActiveTab('apps');
      setSearchParams(runId ? { runId } : {}, { replace: true });
    }
  }, [appIdParam, run, loading, runId, setSearchParams]);

  // The drawer has been opened for the focused app — clear the request so a later
  // re-render (or poll) doesn't reopen it, and so re-selecting the SAME app works.
  const handleFocusHandled = useCallback(() => setFocusAppId(undefined), []);

  const handleSelectRun = (id: string) => {
    setSearchParams({ runId: id });
  };

  const handleCancel = async () => {
    if (!run) return;
    try {
      await cancelRun(run.id);
      setRun((prev: AgentRunDetail | null) => prev ? { ...prev, status: 'cancelled' as const } : prev);
    } catch { /* ignore */ }
  };

  const handleAppUpdated = (appId: string, patch: Partial<AgentRunApp>) => {
    setRun((prev: AgentRunDetail | null) => {
      if (!prev) return prev;
      return {
        ...prev,
        apps: prev.apps.map((a: AgentRunApp) => a.id === appId ? { ...a, ...patch } : a),
      };
    });
  };

  const handleThemeAppClick = (appId: string) => {
    setFocusAppId(appId);
    setActiveTab('apps');
  };

  const handleExportXlsx = async () => {
    if (!run) return;
    setXlsxDownloading(true);
    try {
      const { blob, filename } = await downloadXlsx(run.id);
      triggerBlobDownload(blob, filename);
    } catch {
      // silently ignore — user can retry
    } finally {
      setXlsxDownloading(false);
    }
  };

  const handleExportPptx = async () => {
    if (!runReport.artifact) return;
    setPptxDownloading(true);
    try {
      await downloadReport(runReport.artifact.id, 'pptx');
    } catch {
      /* non-fatal; button re-enables */
    } finally {
      setPptxDownloading(false);
    }
  };

  const handleExportDocx = async () => {
    if (!runReport.artifact) return;
    setDocxDownloading(true);
    try {
      await downloadReport(runReport.artifact.id, 'docx');
    } catch {
      /* non-fatal */
    } finally {
      setDocxDownloading(false);
    }
  };

  // Load this run's cached report deliverable (for the download buttons).
  useEffect(() => {
    if (run?.id && run.status === 'completed') {
      getRunCurrent(run.id).then(setRunReport).catch(() => setRunReport({ artifact: null, stale: false }));
    } else {
      setRunReport({ artifact: null, stale: false });
    }
  }, [run?.id, run?.status]);

  const handleImportApplied = async () => {
    if (!run) return;
    try {
      const refreshed = await getRun(run.id);
      setRun(refreshed);
    } catch { /* ignore */ }
  };

  // Administrator-only: permanently delete this run and ALL AI-produced content
  // (per-app dispositions, themes, action plan, executive summary — cascades in the DB).
  const handleDeleteRun = async () => {
    if (!run) return;
    const ok = window.confirm(
      `Permanently delete the analysis run “${run.name}”?\n\n` +
      `This removes ALL AI-generated content for this run — every application disposition, ` +
      `theme, action-plan item and the executive summary. This cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteRun(run.id);
      // Back to the Run Analysis list; the deleted run is gone.
      navigate('/apr/application-evaluation/disposition-intelligence/run-analysis');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete the run.');
      setDeleting(false);
    }
  };

  const isCompleted = run?.status === 'completed';
  const notCompletedTitle = 'Available once the run is complete.';

  /* Page header actions */
  const headerActions = (
    <>
      {/* Always offer the way back — this screen is usually entered by drilling in
          from the Analysis Repository, which otherwise has no return path. Rendered
          outside the `run` guard so it's there on the run list too. */}
      <button
        className="rd-btn rd-btn-ghost"
        onClick={() => navigate('/apr/application-evaluation/disposition-intelligence/analysis-repository')}
        title="Back to the Analysis Repository"
      >
        <i className="material-icons">arrow_back</i>
        Back to Analysis Repository
      </button>

      {run && (<>
      {/* Download Word — the cached deliverable (generated in the Report Library) */}
      <span style={{ display: 'inline-flex' }}>
        <button
          className="rd-btn rd-btn-word"
          onClick={handleExportDocx}
          disabled={!isCompleted || !runReport.artifact?.docx_key || docxDownloading}
          title={!isCompleted ? notCompletedTitle : (runReport.artifact ? `Download the Word report (generated ${new Date(runReport.artifact.generated_at).toLocaleDateString()})` : 'Report not generated yet — generate it in the Report Library')}
        >
          <i className="material-icons">description</i>
          {docxDownloading ? 'Downloading…' : 'Download Word'}
        </button>
      </span>

      {/* Download PPT */}
      <span style={{ display: 'inline-flex' }}>
        <button
          className="rd-btn rd-btn-ppt"
          onClick={handleExportPptx}
          disabled={!isCompleted || !runReport.artifact?.pptx_key || pptxDownloading}
          title={!isCompleted ? notCompletedTitle : (runReport.artifact ? `Download the PowerPoint report (generated ${new Date(runReport.artifact.generated_at).toLocaleDateString()})` : 'Report not generated yet — generate it in the Report Library')}
        >
          <i className="material-icons">slideshow</i>
          {pptxDownloading ? 'Downloading…' : 'Download PPT'}
        </button>
      </span>

      {/* Import edits from Excel */}
      <span title={!isCompleted ? notCompletedTitle : 'Import edits from Excel'} style={{ display: 'inline-flex' }}>
        <button
          className="rd-btn rd-btn-secondary"
          onClick={() => setShowImportModal(true)}
          disabled={!isCompleted}
          title={!isCompleted ? notCompletedTitle : 'Import edits from Excel'}
        >
          <i className="material-icons">upload_file</i>
          Import Edits
        </button>
      </span>

      {/* Administrator-only: delete this run and all of its AI-produced content */}
      {isAdmin && (
        <button
          className="rd-btn rd-btn-secondary"
          onClick={handleDeleteRun}
          disabled={deleting}
          title="Delete this run and all its AI analysis results (Administrator only)"
          style={{ color: '#c62828' }}
        >
          <i className="material-icons">delete_outline</i>
          {deleting ? 'Deleting…' : 'Delete Run'}
        </button>
      )}
      </>)}
    </>
  );

  const scopeSummary = run
    ? `${run.scope === 'portfolio' ? 'Full portfolio' : 'Subset'} · ${run.apps.length} app${run.apps.length !== 1 ? 's' : ''}`
    : 'Per-application recommendations, vendor-family rollups, action plans, and exports.';

  return (
    <div className="page ai-results-page page-fixed-header">
      <HelpPanel help={AnalysisResultsHelp} />
      <PageHeader
        title={run ? `Analysis Results: ${run.name}` : 'Analysis Results'}
        subtitle={scopeSummary}
        actions={headerActions}
      />

      {/* No runId — show list */}
      {!runId && <RunList onSelect={handleSelectRun} />}

      {/* Loading */}
      {runId && loading && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9e9e9e' }}>Loading run…</div>
      )}

      {/* Error */}
      {runId && error && (
        <div style={{ margin: 20, padding: '14px 18px', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 8, color: '#c62828' }}>
          {error}
        </div>
      )}

      {/* Run loaded */}
      {run && !loading && (
        <>
          {/* Pinned: status, savings and the tab bar never scroll away —
              only the tab body below moves. */}
          <div className="fixed-header-section">
          {/* Status banner */}
          <StatusBanner run={run} onCancel={handleCancel} />

          {/* AI Suggestions (only when completed) */}
          {run.status === 'completed' && <AISuggestionsPanel apps={run.apps} />}

          {/* Tab bar — flexShrink:0 is critical: .page is a fixed-height flex
              column with overflow-y:auto, and Safari/WebKit will otherwise shrink
              this (small) flex item to zero height, collapsing it into a thin
              ribbon. Sticky keeps it visible while the results scroll. */}
          <div style={{
            display: 'flex',
            gap: 0,
            padding: '12px 20px 0',
            borderBottom: '1px solid #e0e0e0',
            overflowX: 'auto',
            position: 'sticky',
            top: 0,
            zIndex: 5,
            background: '#fff',
            flexShrink: 0,
            minHeight: 46,
          }}>
            {TABS.map(tab => (
              <div key={tab.key} style={{ display: 'inline-flex', alignItems: 'center' }}>
              <button
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab.key ? '2px solid #1565c0' : '2px solid transparent',
                  color: activeTab === tab.key ? '#1565c0' : '#666',
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  fontSize: 13,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontFamily: 'inherit',
                  transition: 'color 0.15s ease, border-color 0.15s ease',
                  marginBottom: -1,
                }}
              >
                <i className="material-icons" style={{ fontSize: 16 }}>{tab.icon}</i>
                {tab.label}
                {tab.key === 'apps' && (
                  <span style={{
                    marginLeft: 4,
                    fontSize: 11,
                    background: activeTab === 'apps' ? '#e3f2fd' : '#f5f5f5',
                    color: activeTab === 'apps' ? '#1565c0' : '#9e9e9e',
                    borderRadius: 10,
                    padding: '1px 6px',
                    fontWeight: 600,
                  }}>
                    {run.apps.length}
                  </span>
                )}
              </button>
              <span style={{ marginLeft: 2, marginRight: 4, display: 'inline-flex' }}>
                <InfoTip title={tab.label}>{tab.desc}</InfoTip>
              </span>
              </div>
            ))}
          </div>

          </div>

          {/* Tab body — the ONLY thing that scrolls. A flex column (like the tab bar
              above) stretches full-width in Safari, whereas a plain block child sizes
              to its content. */}
          <div className="scrollable-data-section">
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
            {activeTab === 'summary' && (
              <ExecSummaryTab
                run={run}
                onViewApps={() => setActiveTab('apps')}
              />
            )}
            {activeTab === 'apps' && (
              <AppsTab
                apps={run.apps}
                runId={run.id}
                onUpdated={handleAppUpdated}
                focusAppId={focusAppId}
                onFocusHandled={handleFocusHandled}
              />
            )}
            {activeTab === 'themes' && (
              <ThemesTab
                themes={run.themes}
                apps={run.apps}
                onAppClick={handleThemeAppClick}
              />
            )}
            {activeTab === 'plan' && <ActionPlanTab items={run.actionPlan} />}
            {activeTab === 'methodology' && <MethodologyTab run={run} />}
          </div>
          </div>
        </>
      )}

      {/* Import Modal */}
      {showImportModal && run && (
        <ImportModal
          runId={run.id}
          onClose={() => setShowImportModal(false)}
          onApplied={handleImportApplied}
        />
      )}
    </div>
  );
};

export default AnalysisResults;
